import type { LocaleCode } from '@mybonet/i18n'

export type LegalDocId = 'privacy' | 'terms' | 'deletion'

export type LegalSection = { heading: string; paragraphs: string[] }

export type LegalDoc = {
  title: string
  updated: string
  intro: string
  sections: LegalSection[]
}

const privacyZh: LegalDoc = {
  title: '隱私權政策',
  updated: '最後更新：2026-08-06',
  intro:
    'mybonet（網域 mybo.net，以下稱「本平台」）重視您的隱私。本政策說明我們如何蒐集、使用、保存與保護個人資料，以及您可行使的權利。使用本平台即表示您已閱讀並理解本政策。',
  sections: [
    {
      heading: '1. 資料控管者',
      paragraphs: [
        '本平台由 mybonet 營運。隱私相關詢問可透過網站「聯絡我們」表單提出。',
      ],
    },
    {
      heading: '2. 我們蒐集的資料',
      paragraphs: [
        '帳號資料：註冊／登入時的 Email、顯示名稱、密碼雜湊（我們不以明文保存密碼）。',
        '服務使用資料：您建立的網站、內容、設定、訂閱與帳單紀錄（若有付費方案）。',
        '聯絡表單：姓名、Email、主旨與訊息內容；送出前會進行 Email 驗證。',
        '技術資料：IP、瀏覽器類型、裝置資訊、存取紀錄與錯誤日誌，用於安全與維運。',
        'Cookie／本機儲存：語系偏好、登入工作階段等必要資訊。',
      ],
    },
    {
      heading: '3. 使用目的',
      paragraphs: [
        '提供、維護與改善平台功能（開站、內容管理、金流模組等）。',
        '身分驗證、防濫用、資安偵測與故障排除。',
        '處理客服與業務聯繫、寄送驗證碼或服務通知。',
        '履行合約、會計與法令義務。',
      ],
    },
    {
      heading: '4. 保存期間',
      paragraphs: [
        '帳號與服務資料於帳號存續期間保存；帳號刪除後依「會員刪除機制」處理。',
        '聯絡表單與客服紀錄原則保存至處理完成後最多 24 個月，法令另有規定者從其規定。',
        '系統日誌通常保留較短期間（例如 30–90 天），除非因資安調查需要延長。',
      ],
    },
    {
      heading: '5. 分享與委外',
      paragraphs: [
        '我們不會出售您的個人資料。',
        '可能與必要之雲端基礎設施、郵件寄送、金流或分析服務供應商分享處理所需之最小資料，並要求其僅依指示處理。',
        '依法令、法院或主管機關要求時，得依法提供。',
      ],
    },
    {
      heading: '6. 國際傳輸',
      paragraphs: [
        '本平台基礎設施可能位於台灣以外地區（例如雲端區域）。我們會採取合理措施保護傳輸中的資料。',
      ],
    },
    {
      heading: '7. 您的權利',
      paragraphs: [
        '您可查詢、更正帳號資料，或依「會員刪除機制」申請刪除帳號。',
        '您可拒絕非必要 Cookie／行銷通訊（若日後啟用）；必要服務通知可能仍會寄送。',
      ],
    },
    {
      heading: '8. 政策變更',
      paragraphs: [
        '我們可能更新本政策；重大變更將於本頁更新「最後更新」日期。請定期查閱。',
      ],
    },
  ],
}

const termsZh: LegalDoc = {
  title: '服務條款',
  updated: '最後更新：2026-08-06',
  intro:
    '歡迎使用 mybonet。使用本平台前請詳讀本條款。註冊、登入或使用服務，即表示您同意受本條款拘束。',
  sections: [
    {
      heading: '1. 服務內容',
      paragraphs: [
        'mybonet 提供網站建置與管理平台：您可於 mybo.net 登入後建立與管理網站、編輯內容、模組與相關設定。',
        '功能、方案與定價以網站當時公告為準，我們得合理調整並事先於平台揭示。',
      ],
    },
    {
      heading: '2. 帳號與安全',
      paragraphs: [
        '您應提供正確資料、妥善保管登入憑證，並對帳號下之行為負責。',
        '發現未經授權使用請立即通知我們並變更密碼。',
        '我們得因資安、濫用或違規暫停或終止帳號。',
      ],
    },
    {
      heading: '3. 可接受使用',
      paragraphs: [
        '禁止利用本平台從事違法、侵權、詐欺、散布惡意軟體、濫發訊息或侵害他人權利之行為。',
        '您上傳或發布之內容須有合法權利；內容責任由您自行承擔。',
        '您網站對外呈現之品牌、商品與聲明，不代表 mybonet 背書。',
      ],
    },
    {
      heading: '4. 費用與付款',
      paragraphs: [
        '付費方案依訂閱期間計費；未付款可能導致功能受限或服務中止。',
        '退款政策以結帳頁或方案說明為準；法令強制規定除外。',
      ],
    },
    {
      heading: '5. 智慧財產',
      paragraphs: [
        '平台軟體、商標與設計歸 mybonet 或其授權人所有。',
        '您保有自己內容之權利，並授權我們為提供服務之必要範圍內儲存與處理該內容。',
      ],
    },
    {
      heading: '6. 免責與責任限制',
      paragraphs: [
        '服務依「現況」提供。我們盡力維持可用，但不保證絕對不中斷或無錯誤。',
        '在法律允許最大範圍內，mybonet 對間接、附帶或逸失利益損害不負責任；直接損害責任以您過去十二個月實際支付予本平台之費用為上限（免費使用者則為零）。',
      ],
    },
    {
      heading: '7. 終止',
      paragraphs: [
        '您得隨時停止使用並依會員刪除機制申請刪除帳號。',
        '我們得因重大違約、長期未使用或法規要求終止服務。',
      ],
    },
    {
      heading: '8. 準據法',
      paragraphs: [
        '本條款以中華民國（台灣）法律為準據法；爭議由台灣台北地方法院為第一審管轄法院（法律另有強制規定者從其規定）。',
      ],
    },
  ],
}

const deletionZh: LegalDoc = {
  title: '會員刪除機制',
  updated: '最後更新：2026-08-06',
  intro:
    '本頁說明如何申請刪除 mybonet 帳號及相關資料處理方式，以符合個人資料保護相關要求。',
  sections: [
    {
      heading: '1. 申請方式',
      paragraphs: [
        '請登入 mybo.net 後於帳號管理提出刪除申請，或透過「聯絡我們」表單，主旨註明「帳號刪除」，並使用該帳號綁定之 Email 完成驗證。',
        '為防止冒用，我們可能要求額外身分確認。',
      ],
    },
    {
      heading: '2. 處理時程',
      paragraphs: [
        '驗證身分後，原則於 14 個日曆天內完成刪除或進入不可逆之刪除流程。',
        '若帳號仍有未結清費用、爭議交易或法律保全需求，得暫緩至事由消除。',
      ],
    },
    {
      heading: '3. 刪除範圍',
      paragraphs: [
        '帳號基本資料（Email、顯示名稱、登入工作階段）。',
        '您建立之網站設定與內容（在技術可行範圍內）；已對外快取之 CDN／搜尋引擎副本可能需時間失效。',
        '若網站綁定自有網域或第三方金流帳號，您需自行至該服務商停用或轉移。',
      ],
    },
    {
      heading: '4. 無法立即刪除之資料',
      paragraphs: [
        '依法須保存之交易、發票、審計與資安紀錄，於法定或合理保存期屆滿後刪除或匿名化。',
        '已備份至離線／災難復原媒體之資料，將於既定備份週期覆寫時一併清除。',
      ],
    },
    {
      heading: '5. 刪除後效果',
      paragraphs: [
        '帳號將無法登入 mybo.net；相關訂閱視為終止（未使用期間是否退款依服務條款與方案說明）。',
        '刪除完成後無法還原，請事先自行匯出需要保留之內容。',
      ],
    },
  ],
}

const privacyEn: LegalDoc = {
  title: 'Privacy Policy',
  updated: 'Last updated: 2026-08-06',
  intro:
    'mybonet (mybo.net, “we”) respects your privacy. This policy explains what we collect, how we use it, and your rights. By using the platform you acknowledge this policy.',
  sections: [
    {
      heading: '1. Controller',
      paragraphs: [
        'The platform is operated by mybonet. Privacy requests can be sent via the Contact form on this site.',
      ],
    },
    {
      heading: '2. Data we collect',
      paragraphs: [
        'Account data: email, display name, and password hash (we never store plain-text passwords).',
        'Service data: sites you create, content, settings, and billing records when applicable.',
        'Contact form: name, email, subject, and message (email verification required before send).',
        'Technical data: IP, browser/device info, access and error logs for security and operations.',
        'Cookies / local storage: locale preference and session tokens as needed.',
      ],
    },
    {
      heading: '3. Purposes',
      paragraphs: [
        'Provide and improve the platform (provisioning, CMS, modules, payments).',
        'Authentication, abuse prevention, security, and troubleshooting.',
        'Customer support, verification codes, and service notices.',
        'Contract performance, accounting, and legal compliance.',
      ],
    },
    {
      heading: '4. Retention',
      paragraphs: [
        'Account data is kept while the account is active; after deletion we follow the Account Deletion page.',
        'Contact/support records are typically kept up to 24 months after resolution unless law requires longer.',
        'Operational logs are usually kept 30–90 days unless needed for security investigations.',
      ],
    },
    {
      heading: '5. Sharing',
      paragraphs: [
        'We do not sell personal data.',
        'We may share the minimum necessary data with infrastructure, email, payment, or analytics processors under instruction.',
        'We may disclose data when required by law or competent authorities.',
      ],
    },
    {
      heading: '6. International transfers',
      paragraphs: [
        'Infrastructure may run outside Taiwan. We apply reasonable safeguards for data in transit and at rest.',
      ],
    },
    {
      heading: '7. Your rights',
      paragraphs: [
        'You may access or correct account data and request deletion via the Account Deletion process.',
        'You may opt out of non-essential marketing; essential service messages may still be sent.',
      ],
    },
    {
      heading: '8. Changes',
      paragraphs: [
        'We may update this policy and will revise the “Last updated” date on this page.',
      ],
    },
  ],
}

const termsEn: LegalDoc = {
  title: 'Terms of Service',
  updated: 'Last updated: 2026-08-06',
  intro:
    'Welcome to mybonet. By registering or using the service you agree to these terms.',
  sections: [
    {
      heading: '1. The service',
      paragraphs: [
        'mybonet is a website platform: after signing in at mybo.net you can create and manage sites, content, modules, and related settings.',
        'Features, plans, and pricing are as published on the site and may change with notice on the platform.',
      ],
    },
    {
      heading: '2. Accounts',
      paragraphs: [
        'Provide accurate information, keep credentials secure, and remain responsible for activity under your account.',
        'Notify us of unauthorized use and change your password promptly.',
        'We may suspend or terminate accounts for security, abuse, or breach of these terms.',
      ],
    },
    {
      heading: '3. Acceptable use',
      paragraphs: [
        'Do not use the platform for illegal, infringing, fraudulent, malware, spam, or rights-violating activity.',
        'You must have rights to content you upload; you are responsible for that content.',
        'Brands, products, and statements on your sites are yours, not endorsements by mybonet.',
      ],
    },
    {
      heading: '4. Fees',
      paragraphs: [
        'Paid plans are billed per the selected period; non-payment may limit or stop service.',
        'Refunds follow checkout/plan terms except where mandatory law applies.',
      ],
    },
    {
      heading: '5. Intellectual property',
      paragraphs: [
        'Platform software, marks, and design belong to mybonet or its licensors.',
        'You retain rights in your content and grant us a limited license to host and process it to provide the service.',
      ],
    },
    {
      heading: '6. Disclaimers and liability',
      paragraphs: [
        'Service is provided “as is”. We aim for availability but do not warrant uninterrupted or error-free operation.',
        'To the fullest extent permitted by law, mybonet is not liable for indirect or consequential damages; direct liability is capped at fees you paid us in the prior 12 months (zero for free users).',
      ],
    },
    {
      heading: '7. Termination',
      paragraphs: [
        'You may stop using the service and request account deletion at any time.',
        'We may terminate for material breach, prolonged inactivity, or legal requirements.',
      ],
    },
    {
      heading: '8. Governing law',
      paragraphs: [
        'These terms are governed by the laws of Taiwan (R.O.C.). Taipei District Court has first-instance jurisdiction unless mandatory law provides otherwise.',
      ],
    },
  ],
}

const deletionEn: LegalDoc = {
  title: 'Account Deletion',
  updated: 'Last updated: 2026-08-06',
  intro:
    'This page explains how to request deletion of your mybonet account and how related data is handled.',
  sections: [
    {
      heading: '1. How to request',
      paragraphs: [
        'Sign in at mybo.net and request deletion from account settings, or use Contact with subject “Account deletion” and verify with the account email.',
        'We may ask for extra verification to prevent abuse.',
      ],
    },
    {
      heading: '2. Timeline',
      paragraphs: [
        'After identity verification, we aim to complete deletion (or start irreversible deletion) within 14 calendar days.',
        'Deletion may be delayed for unpaid balances, payment disputes, or legal holds.',
      ],
    },
    {
      heading: '3. What is deleted',
      paragraphs: [
        'Account profile data (email, display name, sessions).',
        'Site settings and content you created, where technically feasible; CDN/search caches may take time to expire.',
        'Custom domains or third-party payment accounts must be closed or transferred by you with those providers.',
      ],
    },
    {
      heading: '4. What may be retained temporarily',
      paragraphs: [
        'Transaction, invoice, audit, and security records required by law, until the retention period ends, then deleted or anonymized.',
        'Offline/disaster-recovery backups are cleared on the normal backup rotation cycle.',
      ],
    },
    {
      heading: '5. After deletion',
      paragraphs: [
        'You will no longer be able to sign in at mybo.net; subscriptions end (refunds follow Terms and plan rules).',
        'Deletion is irreversible — export any content you need beforehand.',
      ],
    },
  ],
}

const docs: Record<LegalDocId, { 'zh-TW': LegalDoc; en: LegalDoc }> = {
  privacy: { 'zh-TW': privacyZh, en: privacyEn },
  terms: { 'zh-TW': termsZh, en: termsEn },
  deletion: { 'zh-TW': deletionZh, en: deletionEn },
}

export function getLegalDoc(id: LegalDocId, locale: string): LegalDoc {
  const pack = docs[id]
  const code = (locale || 'zh-TW') as LocaleCode
  if (code === 'zh-TW' || code === 'zh-CN') return pack['zh-TW']
  return pack.en
}
